import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import * as api from "../../api";
import {useTranslation} from "react-i18next";
import {useFeedbackTools} from "../FeedbackTools";

export const useAclQuery = (principalType, principalId, resourcesType, resourceId, aclQueryFilter) => {
    if (principalId !== undefined && resourceId !== undefined) {
        throw new Error("principalId and resourceId cannot be both defined");
    }
    if (principalId === undefined && resourceId === undefined) {
        throw new Error("principalId or resourceId must be defined");
    }

    let apiFn = null;
    let queryKey = null;

    if (principalId) {
        switch (`${principalType},${resourcesType}`) {
            case 'user,station':
                apiFn = async (filter) =>  await  api.ACL.getUserStationsAcl(principalId, filter);
                queryKey = ['acl', 'users', principalId, 'stations', aclQueryFilter];
                break;
            case 'user,workgroup':
                apiFn = async (filter) =>  await  api.ACL.getUserWorkgroupsAcl(principalId, filter);
                queryKey = ['acl', 'users', principalId, 'workgroups', aclQueryFilter];
                break;
            case 'group,station':
                apiFn = async (filter) =>  await  api.ACL.getGroupStationsAcl(principalId, filter);
                queryKey = ['acl', 'groups', principalId, 'stations', aclQueryFilter];
                break;
            default:
                throw new Error(`Invalid principalType or resourcesType: ${principalType}, ${resourcesType}`);
        }
    }
    if (resourceId) {
        switch (`${principalType},${resourcesType}`) {
            case 'user,station':
                apiFn = async (filter) =>  await api.ACL.getStationAcl(resourceId, filter);
                queryKey = ['acl', 'stations', resourceId, 'users', aclQueryFilter];
                break;
            case 'user,workgroup':
                apiFn = async (filter) =>  await api.ACL.getWorkgroupAcl(resourceId, filter);
                queryKey = ['acl', 'workgroups', resourceId, 'users', aclQueryFilter];
                break;
            case 'group,station':
                apiFn = async (filter) =>  await api.ACL.getStationAcl(resourceId, filter);
                queryKey = ['acl', 'stations', resourceId, 'groups', aclQueryFilter];
                break;
            default:
                throw new Error(`Invalid principalType or resourcesType: ${principalType}, ${resourcesType}`);
        }
    }

    return useQuery({
        queryKey: queryKey,
        queryFn: () => apiFn(aclQueryFilter),
        keepPreviousData: true
    });
}

export const useAclMutation = (principalType, principalId, resourcesType, onSuccess= f=>f) => {
    const {t} = useTranslation(["acl"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    let apiCreateFn = null;
    let apiDeleteFn = null;
    switch (resourcesType) {
        case 'station':
            apiCreateFn = api.ACL.createStationRule;
            apiDeleteFn = api.ACL.deleteStationRule;
            break;
        case 'workgroup':
            apiCreateFn = api.ACL.createWorkgroupRule;
            apiDeleteFn = api.ACL.deleteWorkgroupRule;
            break;
        default:
            throw new Error(`Invalid resourcesType: ${resourcesType}`);
    }

    return useMutation({
        mutationFn: async ({additions, removals}) => {
            // await all additions and removals
            await Promise.all([
                ...additions.map(rule => apiCreateFn(rule.resourceId, rule)),
                ...removals.map(rule => apiDeleteFn(rule.id))
            ]);
        },
        onSuccess: () => {
            onSuccess();
            queryClient.invalidateQueries(['acl']);  // invalidate all acl queries
            showSuccessNotification(t('query.accessRulesUpdateSuccess'));
        }
    });
}